/**
 * SFrame198 – Settle Debts, Expenses & Taxes: Phase Overview
 */

import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { PhaseOverview, type PhaseStepState } from '../settlement';
import { useSettlementSession } from '../../hooks/useSettlementSession';
import { SETTLEMENT_PHASE_CONFIGS } from '../../utils/settlementFlowConfig';
import { SETTLEMENT_PLANNER_LAYOUT } from '../settlement/settlementTheme';

const config = SETTLEMENT_PHASE_CONFIGS['debts-taxes'];

const stepRoutes: Record<string, string> = {
  'dt-1': '/sframe-199',
  'dt-2': '/sframe-202',
  'dt-3': '/sframe-204',
  'dt-4': '/sframe-207',
  'dt-5': '/sframe-215',
};

export default function SFrame198() {
  const { estateId } = useParams<{ estateId: string }>();
  const navigate = useNavigate();
  const { loadPhaseProgress, error } = useSettlementSession(estateId ?? null);

  const [steps, setSteps] = useState<PhaseStepState[]>(
    config.steps.map((step) => ({
      step,
      status: 'not-started',
      subTaskProgress: {
        completed: 0,
        total: step.subTasks?.reduce((sum, st) => sum + (st.checklistItems?.length || 0), 0) || 0,
      },
    }))
  );

  useEffect(() => {
    if (!estateId) return;
    const load = async () => {
      const progressData = await loadPhaseProgress('debts-taxes');
      setSteps(
        config.steps.map((step) => {
          const progress = progressData.find((p) => p.stepId === step.id);
          const totalItems =
            step.subTasks?.reduce((sum, st) => sum + (st.checklistItems?.length || 0), 0) || 0;
          return {
            step,
            status: (progress?.status as PhaseStepState['status']) || 'not-started',
            subTaskProgress: {
              completed: progress?.completedChecklistItems?.length || 0,
              total: totalItems,
            },
          };
        })
      );
    };
    load();
  }, [estateId, loadPhaseProgress]);

  const handleStepClick = (stepId: string) => {
    const route = stepRoutes[stepId];
    if (route) navigate(estateId ? `${route}/${estateId}` : route);
  };

  return (
    <>
      {error && (
        <div className={`${SETTLEMENT_PLANNER_LAYOUT.container} mb-4`}>
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm">{error}</div>
        </div>
      )}
      <PhaseOverview
        title="Settle Debts, Expenses & Taxes"
        subtitle="Identify, validate, pay all estate obligations, and satisfy tax requirements."
        layoutVariant="planner"
        sectionOrder="next-first"
        steps={steps}
        estimatedMinutes={0}
        onStepClick={handleStepClick}
      />
    </>
  );
}
